import type { TypedSupabaseClient } from '../client';
import type {
  BusinessGoal,
  BusinessGoalFilters,
  GoalMetric,
  GoalPeriodType,
  IBusinessGoalsRepository,
  UpsertBusinessGoalInput,
} from '../../../interfaces/src';

const TABLE = 'business_goals';
const DEFAULT_METRIC: GoalMetric = 'revenue';

/** Unique index backing every upsert — (tenant, metric, type, key). */
const CONFLICT_TARGET = 'tenant_id,metric,period_type,period_key';

function mapGoal(row: Record<string, unknown>): BusinessGoal {
  return {
    id: row['id'] as string,
    metric: row['metric'] as GoalMetric,
    periodType: row['period_type'] as GoalPeriodType,
    periodKey: row['period_key'] as string,
    // NUMERIC comes back as a string over PostgREST when it exceeds the JS
    // safe range, and as a number otherwise — normalise so the domain never
    // has to guess (a string target would make `achieved / target` produce
    // NaN and blank the gauge).
    targetAmount: Number(row['target_amount'] ?? 0),
    notes: (row['notes'] as string | null) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

export class SupabaseBusinessGoalsRepository implements IBusinessGoalsRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: BusinessGoalFilters): Promise<BusinessGoal[]> {
    let query = this.supabase
      .from(TABLE)
      .select('*')
      .eq('metric', filters?.metric ?? DEFAULT_METRIC);

    if (filters?.periodKeyPrefix) {
      // `'2026'` matches the year row and its twelve months in one round-trip.
      query = query.like('period_key', `${filters.periodKeyPrefix}%`);
    }

    const { data, error } = await query.order('period_key', { ascending: true });
    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map(mapGoal);
  }

  async upsert(input: UpsertBusinessGoalInput): Promise<BusinessGoal> {
    const [row] = await this.upsertMany([input]);
    if (!row) throw new Error('business_goal_upsert_failed');
    return row;
  }

  async upsertMany(inputs: UpsertBusinessGoalInput[]): Promise<BusinessGoal[]> {
    if (inputs.length === 0) return [];

    const rows = inputs.map((input) => ({
      metric: input.metric ?? DEFAULT_METRIC,
      period_type: input.periodType,
      period_key: input.periodKey,
      target_amount: input.targetAmount,
      notes: input.notes ?? null,
      updated_at: new Date().toISOString(),
    }));

    // tenant_id is left to the BEFORE INSERT trigger — it is also what the RLS
    // WITH CHECK compares against, so sending it from the client would only
    // add a way to get it wrong.
    const { data, error } = await this.supabase
      .from(TABLE)
      .upsert(rows as never, { onConflict: CONFLICT_TARGET })
      .select();

    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map(mapGoal);
  }

  async remove(
    periodType: GoalPeriodType,
    periodKey: string,
    metric: GoalMetric = DEFAULT_METRIC,
  ): Promise<void> {
    const { error } = await this.supabase
      .from(TABLE)
      .delete()
      .eq('metric', metric)
      .eq('period_type', periodType)
      .eq('period_key', periodKey);

    if (error) throw error;
  }
}
